import { useCallback, useEffect } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import type { SelectChangeEvent } from '@mui/material';
import { DEFAULT_ROWS_PER_PAGE } from '@/constant';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { setCurrentHospitalId } from '@/store/slices/hospital';
import { fetchAdminHospitals } from '@/store/slices/hospital-admin/hospital-admin-api';
import { fetchUserPermissions } from '@/store/slices/auth/permissions-api';
import { setProfileDefaultHospital } from '@/store/slices/profile/profile-api';
import { isAdmin, shouldEnableNotifications } from '@/utils/role';
import { getHandoffDashboardExitPath } from './handoffDashboardExit';

const HOSPITAL_CONTEXT_PATH = '/admin/hospitals/:hospitalId/:detailTab/*';

const isWeekendViewPath = (pathname: string) =>
    Boolean(matchPath('/patients/weekend-view/:patientId/:fin/:roomBed', pathname)) ||
    Boolean(matchPath('/admin/hospitals/:hospitalId/manage-patients/weekend-view/:patientId/:fin/:roomBed', pathname));

const disableHospitalSelectorForPaths = (pathname: string) =>
    Boolean(matchPath('/patients/:id', pathname)) ||
    Boolean(matchPath('/admin/hospitals/:hospitalId/manage-patients/:id', pathname)) ||
    Boolean(matchPath('/admin/hospitals/:hospitalId/population-scope/patient-override', pathname)) ||
    Boolean(matchPath('/my-patients/patient-selection', pathname)) ||
    Boolean(matchPath('/admin/hospitals/:hospitalId/my-patients/patient-selection', pathname)) ||
    Boolean(matchPath('/supervisor/scorecard/:userId/:unitId/:date', pathname)) ||
    Boolean(matchPath('/admin/hospitals/:hospitalId/scorecard/:userId/:unitId/:date', pathname)) ||
    Boolean(matchPath('/admin/hospitals/:hospitalId/meeting-view/patient-details/:id', pathname)) ||
    Boolean(matchPath('/admin/hospitals/:hospitalId/meeting-transcription/:sessionId/verify', pathname)) ||
    Boolean(matchPath('/admin/meeting-transcription/:hospitalId/:sessionId/verify', pathname)) ||
    Boolean(matchPath('/meeting-transcription/:hospitalId/:sessionId/verify', pathname)) ||
    Boolean(matchPath('/admin/hospitals/:hospitalId/meeting-transcription/upload', pathname)) ||
    Boolean(matchPath('/admin/meeting-transcription/upload', pathname)) ||
    Boolean(matchPath('/meeting-transcription/upload', pathname));

export default () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { profile } = useAppSelector(({ profile: profileState }) => profileState);
    const permissions = useAppSelector(({ auth }) => auth.permissions);
    const { drawerOpen } = useAppSelector(({ menu }) => menu);
    const { currentHospitalId } = useAppSelector(({ hospital }) => hospital);
    const { hospitals: adminHospitals, hospitalsLoading: adminHospitalsLoading } = useAppSelector(({ hospitalAdmin }) => hospitalAdmin);

    const isAdminUser = isAdmin(profile);
    const shouldShowNotifications = shouldEnableNotifications(profile);
    const externalUserHospitals = profile?.hospitals || [];
    const availableHospitals = isAdminUser ? adminHospitals : externalUserHospitals;
    const hospitalContextRouteMatch = matchPath(HOSPITAL_CONTEXT_PATH, location.pathname);

    const selectedHospitalId =
        (isAdminUser
            ? (hospitalContextRouteMatch?.params?.hospitalId ?? currentHospitalId)
            : externalUserHospitals.find((hospital) => hospital.isDefault)?.id) || '';

    const shouldShowHospitalSelector =
        !isWeekendViewPath(location.pathname) && availableHospitals.length > 0 && (isAdminUser ? Boolean(hospitalContextRouteMatch) : true);
    const isHospitalSelectorDisabled = disableHospitalSelectorForPaths(location.pathname) || availableHospitals.length < 2;

    const handleHospitalChange = useCallback(
        async (event: SelectChangeEvent<string>) => {
            const nextHospitalId = event.target.value;
            const detailTab = hospitalContextRouteMatch?.params?.detailTab;
            const nextHospitalName = availableHospitals.find((hospital) => hospital.id === nextHospitalId)?.name ?? '';
            const exitPath = getHandoffDashboardExitPath({
                pathname: location.pathname,
                hospitalId: nextHospitalId,
                hospitalName: nextHospitalName,
                user: profile,
                permissions
            });

            if (isAdminUser) {
                if (!nextHospitalId) {
                    return;
                }

                dispatch(setCurrentHospitalId(nextHospitalId));

                if (exitPath) {
                    navigate(exitPath, { replace: true });
                    return;
                }

                const hospitalSegmentPrefix = /^\/admin\/hospitals\/[^/]+/;
                if (hospitalSegmentPrefix.test(location.pathname)) {
                    const nextPath = location.pathname.replace(hospitalSegmentPrefix, `/admin/hospitals/${nextHospitalId}`);
                    navigate(`${nextPath}${location.search}`, { replace: true });
                    return;
                }

                if (!detailTab) {
                    return;
                }

                navigate(`/admin/hospitals/${nextHospitalId}/${detailTab}${location.search}`, { replace: true });
                return;
            }

            const { hospitalId } = await dispatch(setProfileDefaultHospital({ hospitalId: nextHospitalId })).unwrap();
            dispatch(setCurrentHospitalId(hospitalId));
            let nextPermissions = permissions;
            try {
                nextPermissions = await dispatch(fetchUserPermissions()).unwrap();
            } catch {
                nextPermissions = permissions;
            }

            const staffExitPath = getHandoffDashboardExitPath({
                pathname: location.pathname,
                hospitalId,
                hospitalName: nextHospitalName,
                user: profile,
                permissions: nextPermissions
            });
            if (staffExitPath) {
                navigate(staffExitPath, { replace: true });
            }
        },
        [
            availableHospitals,
            dispatch,
            hospitalContextRouteMatch,
            isAdminUser,
            location.pathname,
            location.search,
            navigate,
            permissions,
            profile
        ]
    );

    useEffect(() => {
        if (!isAdminUser || !hospitalContextRouteMatch || adminHospitalsLoading || adminHospitals.length > 0) {
            return;
        }

        dispatch(fetchAdminHospitals({ page: 1, limit: DEFAULT_ROWS_PER_PAGE }));
    }, [adminHospitals.length, adminHospitalsLoading, dispatch, hospitalContextRouteMatch, isAdminUser]);

    return {
        dispatch,
        availableHospitals,
        selectedHospitalId,
        shouldShowHospitalSelector,
        shouldShowNotifications,
        isHospitalSelectorDisabled,
        handleHospitalChange,
        drawerOpen
    };
};
